import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { NavigationEnd } from '@angular/router';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';
import { DateEx, WEEKDAYS } from '../../../functions/date-ex';
import {
  clearTaskSearchUpdates,
  rememberTaskSearchUpdate,
} from '../../../functions/todo/search-retain-updates';
import { leaveLink } from '../../../functions/claim/leaveLink';
import { numericAdaOrLeaveKeyFromClaim } from '../../../functions/todo-claim-numeric-key';
import { TaskCountsResponse } from '../../../models/api/todo/task-count-response';
import { TaskRequest } from '../../../models/api/todo/task-request';
import { TaskResponse } from '../../../models/api/todo/task-response';
import { Action } from '../../../models/core/action';
import { TasksService } from '../../../services/tasks/tasks-service';
import { SettingsService } from '../../../services/settings/settings.service';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { PersonalTaskDataService } from '../../../services/todo/personal-task-data.service';
import { BaseComponent } from '../../base/base.component';
import { RadioButtonComponent } from '../../core/controls/radio-button.component';
import { ToggleSwitchComponent } from '../../core/controls/toggle-switch.component';
import { LoadingState } from '../../core/panels/loading-panel.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import { PanelComponent } from '../../core/panels/panel.component';
import { TaskSearchComponent } from './task-search.component';
import { TaskTableComponent } from './task-table.component';
import { TaskWeekComponent } from './task-week.component';
import { TaskMonthsComponent } from './task-months.component';
import { filter } from 'rxjs';
import { TabComponent } from '../../core/panels/tab-panel/tab.component';
import { TabPanelComponent } from '../../core/panels/tab-panel/tab-panel.component';

type MyTasksView = 'week' | '3months' | 'all';

@Component({
  selector: 'app-task-view',
  imports: [
    TaskTableComponent,
    TaskWeekComponent,
    TaskMonthsComponent,
    PanelComponent,
    ButtonComponent,
    EmptyStateComponent,
    TaskSearchComponent,
    TabComponent,
    TabPanelComponent,
    RadioButtonComponent,
    ToggleSwitchComponent,
  ],
  template: `
    <p
      class="tw-italic tw-text-[var(--sed-black-80-color)] tw-text-sm tw-pb-4 tw-pt-2"
    >
      Personal tasks are user-specific and cannot be viewed by other users.
    </p>
    <app-tab-panel [value]="tab.value" (valueChange)="onTabChange($event)">
      <app-tab label="My tasks" [visible]="true" value="browse">
        <ng-template #content>
          @if (viewReady) {
            <div
              class="tw-flex tw-flex-wrap tw-items-end tw-gap-x-8 tw-gap-y-3 tw-pt-6"
            >
              <app-radio-button
                [control]="viewControl"
                [options]="viewOptions"
                optionLabel="label"
                optionValue="value"
                label="View my open tasks for:"
                [vertical]="false"
                [showHelpText]="false"
              />
              <app-toggle-switch
                [control]="includeCompletedControl"
                label="Include completed"
                [showHelpText]="false"
              />
            </div>
          }
          @if (viewReady && viewControl.value === 'week') {
            <div class="tw-py-6">
              <app-task-week
                [items]="filteredCalendarItems"
                [claim]="claim"
                [loading]="browseLoading && !browse.isClndrClicked"
                [isPageTodo]="isPageTodo"
                [weekDate]="weekRestoreDate"
                (calenderAction)="onCalenderAction($event)"
              />
            </div>
          }
          @if (viewReady && viewControl.value === '3months') {
            <div class="tw-py-6">
              <app-task-months
                [items]="filteredCalendarItems"
                [loading]="browseLoading && !browse.isClndrClicked"
                [includeCompleted]="!!includeCompletedControl.value"
                (calenderAction)="onMonthsAction($event)"
              />
            </div>
          }
          <app-panel>
            <app-task-table
              [dataSource]="filteredBrowseTasks"
              [isPageTodo]="isPageTodo"
              newTaskAlign="right"
              [recordPeriod]="weekRecordPeriod"
              [showPageOfTotal]="viewControl.value === 'all'"
              [claim]="claim"
              [loading]="
                browseLoading || (tab.value !== 'search' && tableLoading)
              "
              [error]="browseError"
              (searchCountEvent)="onBrowseCount($event)"
              (markTaskComplete)="markTaskComplete($event)"
            />
          </app-panel>
        </ng-template>
      </app-tab>
      <app-tab label="Search tasks" [visible]="true" value="search">
        <ng-template #content>
          <div class="tw-py-6">
            <app-task-search
              [loading]="
                searchLoading || (tab.value === 'search' && tableLoading)
              "
              [request]="searchTab.request"
              (searchRequest)="searchRequest($event)"
              (clear)="onClearSearch()"
            />
          </div>
          <hr />
          <div class="tw-flex tw-justify-end tw-pt-4 tw-pb-4">
            <app-button label="New Task" (click)="newSearchTask()" />
          </div>
          @if (!searchTab.searched) {
            <app-empty-state
              icon="fa-light fa-magnifying-glass"
              label="Search results"
              message="Search results will appear here"
              height="400px"
            />
          } @else {
            <app-panel>
              <app-task-table
                [dataSource]="filteredSearchTasks"
                [isPageTodo]="isPageTodo"
                [showNewTask]="false"
                [claim]="claim"
                [loading]="
                  searchLoading || (tab.value === 'search' && tableLoading)
                "
                [error]="searchError"
                (searchCountEvent)="onSearchCount($event)"
                (markTaskComplete)="markTaskComplete($event)"
              />
            </app-panel>
          }
        </ng-template>
      </app-tab>
    </app-tab-panel>
  `,
  styles: ``,
})
export class TaskViewComponent extends BaseComponent implements OnInit {
  private tasksService = inject(TasksService);
  private personalTaskData = inject(PersonalTaskDataService);
  private snackbar = inject(Snackbar);
  private settings = inject(SettingsService);

  @Input() claim: any = null;
  @Input() isPageTodo = false;
  @Output() search = new EventEmitter();
  @Output() clear = new EventEmitter();

  loading = false;
  tableLoading = false;
  browseLoading = false;
  searchLoading = false;
  browseError: any = null;
  searchError: any = null;
  error: any = null;
  taskCount: TaskCountsResponse;
  taskResponseData: TaskResponse[];
  pastDue: boolean;
  InitilisingTasks: boolean;
  cameFromTaskEdit = false;
  /** Passed into the week strip only when My tasks is opened again after a tab switch. */
  protected weekRestoreDate: Date = null;
  protected viewReady = false;
  protected viewOptions: { label: string; value: MyTasksView }[] = [];
  protected viewControl = new FormControl<MyTasksView>('week');
  protected includeCompletedControl = new FormControl(false);
  protected tab = new FormControl<string>('browse');
  protected state: LoadingState = 'Loading';
  protected tabs: Action[] = [
    { label: 'Browse', key: 'browse', title: 'Browse Tasks' },
    { label: 'Search', key: 'search', title: 'Search Tasks' },
  ];

  /** My tasks view. Kept separate from search so toggling tabs does not replace results. */
  protected browse = {
    data: [] as TaskResponse[],
    /** Tasks returned for the week currently shown in the calendar. */
    weekData: [] as TaskResponse[],
    /** Tasks due on the selected calendar day. Empty when the full week is shown. */
    dayData: [] as TaskResponse[],
    request: null as TaskRequest,
    count: 0,
    title: '' as string,
    calendarItems: [] as any[],
    selectedDate: null as string,
    /** Clicked calendar day. Cleared when the week changes. */
    selectedDayDate: null as Date,
    weekStartDate: null as Date,
    weekEndDate: null as Date,
    monthStartDate: null as Date,
    monthEndDate: null as Date,
    includeCompleted: false,
    view: 'week' as MyTasksView,
    isClndrClicked: false,
    isMarkCompleteEventFired: false,
  };

  /** Search tasks view. */
  protected searchTab = {
    data: [] as TaskResponse[],
    request: null as TaskRequest,
    count: 0,
    title: '0 Tasks',
    searched: false,
  };

  private _lastCalendarItems: any[] = [];
  private _lastIncludeCompletedCal = false;
  private _lastSelectedDate = '';
  private _lastFilteredCalendarItems: any[] = [];
  private _lastBrowseTaskData: TaskResponse[] = [];
  private _lastBrowseIncludeCompleted = false;
  private _lastBrowseRows: any[] = [];
  private _lastSearchTaskData: TaskResponse[] = [];
  private _lastSearchRequest: TaskRequest = null;
  private _lastSearchRows: any[] = [];
  private searchPagePath = '';

  constructor() {
    super();
    let previousUrl = this.router.url ?? '';
    this.dispose.subs = this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe(async (event) => {
        const nextUrl = event.urlAfterRedirects ?? event.url;
        const fromEdit = previousUrl.startsWith('/task-edit');
        if (fromEdit) {
          await this.refreshActiveTab();
        }
        previousUrl = nextUrl;
      });
  }

  async ngOnInit() {
    this.browseLoading = true;
    this.isPageTodo = document.location.pathname === '/todo';
    this.viewOptions = this.isPageTodo
      ? [
          { label: 'The week', value: 'week' },
          { label: '3 months', value: '3months' },
        ]
      : [
          { label: 'All', value: 'all' },
          { label: 'The week', value: 'week' },
          { label: '3 months', value: '3months' },
        ];
    const saved = this.storage.get(this.tabStorageKey());
    if (saved === 'search' || saved === 'browse') {
      this.tab.setValue(saved, { emitEvent: false });
    }
    const view = await this.readSavedView();
    this.browse.view = view;
    this.viewControl.setValue(view, { emitEvent: false });
    this.dispose.subs = this.viewControl.valueChanges.subscribe((value) => {
      void this.onViewRangeChange(value);
    });
    this.dispose.subs = this.includeCompletedControl.valueChanges.subscribe(
      (value: boolean) => this.onIncludeCompletedChange(!!value),
    );
    this.viewReady = true;
    this.state = 'Loaded';
    this.searchPagePath = document.location.pathname;
    this.restoreSearch();
    this.applySearchPatch();
    if (view === 'all') {
      this.browse.request = this.buildRangeRequest();
      await this.loadBrowse();
    }
  }

  @HandleLoadingState({
    loadingProperty: 'state',
    onValue: 'Loading',
    offValue: 'Loaded',
  })
  @HandleLoadingState({
    loadingProperty: 'browseLoading',
    errorProperty: 'browseError',
  })
  async loadInitialTasks() {
    this.claim = this.view.currentClaim();
    if (!this.browse.request) {
      this.browse.request = this.buildRangeRequest();
    }

    if (!this.isPageTodo && this.claim && this.claim.recordId != null) {
      const claimSpecificFields = this.getClaimSpecificFields();
      Object.assign(this.browse.request, claimSpecificFields);
    }
    this.browse.request.recordSource = this.claim?.recordSource;
    this.taskResponseData = await this.api.task.getTaskEntry(
      this.browse.request,
    );

    this.applyBrowseData(this.taskResponseData);
  }

  @HandleLoadingState({
    loadingProperty: 'state',
    onValue: 'Loading',
    offValue: 'Loaded',
  })
  @HandleLoadingState({
    loadingProperty: 'browseLoading',
    errorProperty: 'browseError',
  })
  async loadBrowse() {
    this.claim = this.view.currentClaim();
    if (!this.isPageTodo && this.claim && this.claim.recordId != null) {
      if (!this.browse.request) {
        this.browse.request = this.buildRangeRequest();
      }

      const claimSpecificFields = this.getClaimSpecificFields();
      Object.assign(this.browse.request, claimSpecificFields);
    }
    if (!this.browse.request) {
      return;
    }
    this.browse.request.recordId = this.claim?.recordID || undefined;
    this.browse.request.recordSource = this.claim?.recordSource || undefined;
    this.taskResponseData = await this.api.task.getTaskEntry(
      this.browse.request,
    );
    this.applyBrowseData(this.taskResponseData);
  }

  @HandleLoadingState({
    loadingProperty: 'state',
    onValue: 'Loading',
    offValue: 'Loaded',
  })
  @HandleLoadingState({
    loadingProperty: 'searchLoading',
    errorProperty: 'searchError',
  })
  async loadSearch() {
    if (!this.searchTab.request) {
      return;
    }
    this.claim = this.view.currentClaim();
    if (!this.isPageTodo && this.claim && this.claim.recordId != null) {
      const claimSpecificFields = this.getClaimSpecificFields();
      Object.assign(this.searchTab.request, claimSpecificFields);
    }
    this.searchTab.request.recordId = this.claim?.recordID || undefined;
    this.searchTab.request.recordSource = this.claim?.recordSource || undefined;
    this.searchTab.searched = true;
    const data = await this.api.task.getTaskEntry(this.searchTab.request);
    this.applySearchData(data);
    clearTaskSearchUpdates(this.storage);
    this.persistSearch();
  }

  prepareCalenderDataSource() {
    const weekMap: any = {};
    for (let i = 0; i < 7; i++) {
      const date = new Date(this.browse.weekStartDate);
      date.setDate(date.getDate() + i);
      const dayKey = WEEKDAYS[date.getDay()];
      weekMap[dayKey] = {
        day: dayKey,
        date: date.toISOString(),
        displayDate: DateEx.formatShortDate(date),
        completed: 0,
        pastDue: 0,
        draft: 0,
        open: 0,
      };
    }
    const today = new Date();
    const startOfWeek = new Date(this.browse.weekStartDate);
    const endOfWeek = new Date(this.browse.weekStartDate);
    endOfWeek.setDate(endOfWeek.getDate() + 6);
    const tasks =
      this.browse.view === 'week' ? this.browse.weekData : this.browse.data;
    for (const task of tasks) {
      const due = new Date(task.dateDue);

      if (!DateEx.dateBetween(due, startOfWeek, endOfWeek)) {
        continue; // Only count tasks in this week
      }

      const dayKey = WEEKDAYS[due.getDay()];
      const item = weekMap[dayKey];

      const itemDate = new Date(item.date);
      if (
        !item ||
        itemDate.getFullYear() !== due.getFullYear() ||
        itemDate.getMonth() !== due.getMonth() ||
        itemDate.getDate() !== due.getDate()
      ) {
        continue;
      }

      const status = (task.status || '').toLowerCase();
      if (status === 'completed') {
        item.completed++;
      } else if (status === 'open') {
        if (DateEx.dateBefore(due, today)) {
          item.pastDue++;
        } else {
          item.open++;
        }
      }
    }
    return Object.values(weekMap);
  }

  prepareMonthsDataSource() {
    const items: {
      dateKey: string;
      date: Date;
      open: number;
      pastDue: number;
      completed: number;
      draft: number;
    }[] = [];
    const byDay = new Map<string, (typeof items)[number]>();
    if (!this.browse.monthStartDate || !this.browse.monthEndDate) {
      return items;
    }
    const cursor = new Date(
      this.browse.monthStartDate.getFullYear(),
      this.browse.monthStartDate.getMonth(),
      this.browse.monthStartDate.getDate(),
    );
    const end = this.browse.monthEndDate;
    while (cursor <= end) {
      const date = new Date(cursor);
      const item = {
        dateKey: `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`,
        date,
        open: 0,
        pastDue: 0,
        completed: 0,
        draft: 0,
      };
      items.push(item);
      byDay.set(item.dateKey, item);
      cursor.setDate(cursor.getDate() + 1);
    }
    const today = new Date();
    for (const task of this.browse.weekData ?? []) {
      const due = new Date(task.dateDue);
      const item = byDay.get(
        `${due.getFullYear()}-${due.getMonth()}-${due.getDate()}`,
      );
      if (!item) {
        continue;
      }
      const status = (task.status || '').toLowerCase();
      if (status === 'completed') {
        item.completed++;
      } else if (status === 'open') {
        if (DateEx.dateBefore(due, today)) {
          item.pastDue++;
        } else {
          item.open++;
        }
      }
    }
    return items;
  }

  applyBrowseData(data: TaskResponse[]) {
    this.browse.data = data;
    this.browse.count = data.length;

    if (this.browse.view === 'week' || this.browse.view === '3months') {
      if (this.browse.isClndrClicked && this.browse.selectedDayDate) {
        this.browse.weekData = data;
        this.browse.dayData = this.filterTasksByDate(
          data,
          this.browse.selectedDayDate,
        );
        this.browse.calendarItems =
          this.browse.view === 'week'
            ? this.prepareCalenderDataSource()
            : this.prepareMonthsDataSource();
      } else if (!this.browse.isClndrClicked) {
        this.browse.weekData = data;
        this.browse.dayData = [];
        this.browse.selectedDayDate = null;
        this.browse.calendarItems =
          this.browse.view === 'week'
            ? this.prepareCalenderDataSource()
            : this.prepareMonthsDataSource();
      }
    }

    this.browse.isMarkCompleteEventFired = false;
    if (this.browse.view === 'week') {
      this.browse.title = this.browse.selectedDate ?? '';
    }
  }

  applySearchData(data: TaskResponse[]) {
    this.searchTab.data = data;
    this.searchTab.count = data.length;
    this.searchTab.title = this.taskCountTitle(data.length);
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async GetTasksCount() {
    this.taskCount = await this.api.task.getTaskCount();
  }

  activeItemDateEvent(dateStr: string) {
    this.browse.selectedDate = dateStr;
    this.browse.title = this.browse.selectedDate;
  }

  searchRequest(request: TaskRequest) {
    this.searchTab.request = request;
    this.loadSearch();
  }

  onCalenderAction(event: {
    action: string;
    weekStartDate: Date;
    weekEndDate: Date;
    claimSpecificFields?: any;
  }) {
    if (this.browse.view !== 'week') {
      return;
    }
    if (event.action === 'calendarClick') {
      const clickedDate = new Date(event.weekStartDate);
      this.browse.isClndrClicked = true;
      this.browse.selectedDayDate = clickedDate;
      this.browse.dayData = this.filterTasksByDate(
        this.browse.weekData,
        clickedDate,
      );
      this.activeItemDateEvent(DateEx.formatShortDate(clickedDate));
      return;
    }
    this.browse.selectedDate = DateEx.formatShortDate(event.weekStartDate);
    this.browse.weekStartDate = DateEx.getStartOfWeek(event.weekStartDate);
    this.browse.weekEndDate = DateEx.getEndOfWeek(this.browse.weekStartDate);
    this.browse.request = this.prepareRequest(
      event.weekStartDate,
      event.weekEndDate,
    );
    this.browse.isClndrClicked = false;
    this.browse.dayData = [];
    this.browse.selectedDayDate = null;
    this.InitilisingTasks = false;
    switch (event.action) {
      case 'previous':
      case 'next': {
        const dateStr = this.browse.selectedDate;
        this.activeItemDateEvent(dateStr);
        this.loadBrowse();
        break;
      }
      case 'init': {
        this.InitilisingTasks = true;
        this.browse.request = this.prepareRequest(
          event.weekStartDate,
          event.weekEndDate,
        );
        this.loadInitialTasks();
        this.activeItemDateEvent(this.browse.selectedDate);
        break;
      }
      case 'today': {
        this.activeItemDateEvent(DateEx.formatShortDate(new Date()));
        this.loadBrowse();
        break;
      }
      default:
        break;
    }
  }

  onMonthsAction(event: { action: string; startDate: Date; endDate: Date }) {
    if (this.browse.view !== '3months') {
      return;
    }
    if (event.action === 'calendarClick') {
      const clickedDate = new Date(event.startDate);
      this.browse.isClndrClicked = true;
      this.browse.selectedDayDate = clickedDate;
      this.browse.dayData = this.filterTasksByDate(
        this.browse.weekData,
        clickedDate,
      );
      this.activeItemDateEvent(DateEx.formatShortDate(clickedDate));
      return;
    }
    this.browse.monthStartDate = new Date(event.startDate);
    this.browse.monthEndDate = new Date(event.endDate);
    this.browse.request = this.prepareRequest(
      this.browse.monthStartDate,
      this.browse.monthEndDate,
    );
    this.browse.isClndrClicked = false;
    this.browse.dayData = [];
    this.browse.selectedDayDate = null;
    this.browse.selectedDate = null;
    this.loadBrowse();
  }

  getStatusLabel(status: string, dueDate: Date): string {
    const now = new Date();
    const due = new Date(dueDate);
    const normalizedStatus = status?.toLowerCase();

    if (normalizedStatus === 'completed') return 'Completed';
    if (normalizedStatus === 'draft') return 'Draft';
    if (DateEx.dateBefore(due, now) && normalizedStatus !== 'completed')
      return 'Past due';
    return 'Open';
  }

  searchEvent(event: string) {
    // TODO
  }

  onBrowseCount(count: number) {
    this.browse.count = count;
  }

  onSearchCount(count: number) {
    this.searchTab.count = count;
    this.searchTab.title = this.taskCountTitle(count);
  }

  @HandleLoadingState({
    loadingProperty: 'tableLoading',
    errorProperty: 'actionError',
  })
  async markTaskComplete(rows: any[]) {
    if (!rows?.length) {
      return;
    }

    for (const data of rows) {
      const updateRequest: TaskRequest = {
        taskUID: data.taskUid,
        completed: true,
        dueDate: data.dueDate,
        title: data.title,
        description: data.message,
        recordId: this.claim?.recordID || undefined,
        recordSource: this.claim?.recordSource || undefined,
      };
      await this.api.task.updateTask(updateRequest);
    }

    this.snackbar.show(
      rows.length > 1 ? 'Tasks completed' : 'Task completed',
      'Success',
    );

    this.personalTaskData.notifyChanged();
    if (this.claim) {
      this.tasksService.deleteTasksByClaim({
        recordId: this.claim.recordId,
        recordSource: this.claim.recordSource,
      });
    }

    if (this.tab.value === 'search') {
      this.applyCompletedToSearch(rows);
      return;
    }

    this.browse.isMarkCompleteEventFired = true;
    this.browse.isClndrClicked = false;
    this.browse.request = this.buildRangeRequest();

    if (!this.isPageTodo && this.claim && this.claim.recordId != null) {
      const claimSpecificFields = this.getClaimSpecificFields();
      Object.assign(this.browse.request, claimSpecificFields);
    }

    await this.loadBrowse();
  }

  prepareRequest(
    weekStartDate: Date | null = null,
    weekEndDate: Date | null = null,
  ): TaskRequest {
    const request: TaskRequest = {
      title: '',
      taskStatus: 0,
      pastDue: false,
      recordSource: this.claim?.recordSource,
      recordId: this.claim?.recordID || undefined,
    };
    if (weekStartDate) {
      request.dueStartDate = DateEx.clearTime(weekStartDate);
    }
    if (weekEndDate) {
      request.dueEndDate = DateEx.clearTime(weekEndDate);
    }
    return request;
  }

  onIncludeCompletedChange(value: boolean) {
    this.browse.includeCompleted = value;
  }

  onClearSearch() {
    clearTaskSearchUpdates(this.storage);
    this.searchTab.data = [];
    this.searchTab.request = null;
    this.searchTab.count = 0;
    this.searchTab.title = '0 Tasks';
    this.searchTab.searched = false;
    this.searchLoading = false;
    this._lastSearchTaskData = [];
    this._lastSearchRequest = null;
    this._lastSearchRows = [];
    this.storage.remove(this.searchStorageKey());
  }

  newSearchTask() {
    if (this.isPageTodo) {
      this.storage.set('current-task', {
        backRouterLink: document.location.pathname,
        backLabel: 'My to-do',
      });
    } else {
      const claim = this.claim || this.view.currentClaim();
      this.storage.set('current-task', {
        backRouterLink: document.location.pathname,
        backLabel: 'My to-do',
        isClaimSpecific: true,
        claimUID: claim?.recordId,
        contractNumber: claim?.contractNumber,
        employeeUID: claim?.hrMasterUid ? Number(claim.hrMasterUid) : null,
      });
    }
    this.router.navigate(['/task-edit']);
  }

  private applyCompletedToSearch(rows: { taskUid: number }[]) {
    const ids = new Set(rows.map((row) => row.taskUid));
    this.searchTab.data = (this.searchTab.data ?? []).filter(
      (task): boolean => {
        if (!ids.has(task.taskUID)) {
          return true;
        }
        const saved = { ...task, status: 'Completed' };
        rememberTaskSearchUpdate(this.storage, {
          taskUid: task.taskUID,
          status: 'Completed',
        });
        const reason = this.taskMismatchReason(saved, this.searchTab.request);
        return reason == null;
      },
    );
    this.searchTab.count = this.searchTab.data.length;
    this.persistSearch();
  }

  private searchPatchKey(): string {
    const page = this.searchPagePath || document.location.pathname;
    return `personal-task-search-patch:${page}`;
  }

  private applySearchPatch(): void {
    const key = this.searchPatchKey();
    const legacyKey = 'personal-task-search-patch';
    const patch = this.storage.get(key) ?? this.storage.get(legacyKey);
    if (!patch) {
      return;
    }
    this.storage.remove(key);
    this.storage.remove(legacyKey);
    if (!this.searchTab.searched || patch.isNew || patch.taskUID == null) {
      return;
    }
    this.searchTab.data = (this.searchTab.data ?? []).flatMap((task) => {
      if (task.taskUID !== patch.taskUID) {
        return [task];
      }
      const saved = this.taskMatchCandidate(task, patch);
      const reason = this.taskMismatchReason(saved, this.searchTab.request);
      return reason == null ? [saved] : [];
    });
    this.searchTab.count = this.searchTab.data.length;
    this.persistSearch();
  }

  private taskMatchCandidate(
    task: TaskResponse,
    patch: {
      title?: string;
      dueDate?: Date | string;
      description?: string;
      completed?: boolean;
      sendEmail?: boolean;
    },
  ): TaskResponse {
    const dueDate = patch.dueDate
      ? (this.savedCalendarDate(patch.dueDate) ?? task.dateDue)
      : task.dateDue;
    let status = task.status;
    if (patch.completed) {
      status = 'Completed';
    } else if ((task.status || '').toLowerCase() === 'completed') {
      status = 'Open';
    }
    return {
      ...task,
      title: patch.title ?? task.title,
      description: patch.description ?? task.description,
      dateDue: dueDate,
      emailReminder: patch.sendEmail ?? task.emailReminder,
      status,
    };
  }

  /** Due dates saved from the edit form are UTC midnight of the chosen day. */
  private savedCalendarDate(value: Date | string): Date | null {
    const parsed = value instanceof Date ? value : new Date(value);
    if (isNaN(parsed.getTime())) {
      return null;
    }
    return new Date(
      parsed.getUTCFullYear(),
      parsed.getUTCMonth(),
      parsed.getUTCDate(),
    );
  }

  private taskMismatchReason(
    task: TaskResponse,
    request: TaskRequest,
  ): string | null {
    if (!request) {
      return null;
    }
    const title = (request.title || '').trim().toLowerCase();
    if (title && !(task.title || '').toLowerCase().includes(title)) {
      return 'title';
    }
    const due = task.dateDue ? new Date(task.dateDue) : null;
    if (request.dueStartDate) {
      if (!due || DateEx.dateBefore(due, new Date(request.dueStartDate))) {
        return 'due-before-start';
      }
    }
    if (request.dueEndDate) {
      if (!due || DateEx.dateBefore(new Date(request.dueEndDate), due)) {
        return 'due-after-end';
      }
    }
    const status = this.getStatusLabel(task.status, task.dateDue);
    switch (request.taskStatus) {
      case 1:
        return status === 'Open' ? null : `status:${status}`;
      case 2:
        return status === 'Completed' ? null : `status:${status}`;
      case 3:
        return status === 'Past due' ? null : `status:${status}`;
      default:
        return null;
    }
  }

  private searchStorageKey(): string {
    if (this.isPageTodo) {
      return 'personal-tasks-search-main';
    }
    const claim = this.view.currentClaim();
    return `personal-tasks-search-claim-${claim?.recordSource ?? ''}-${claim?.recordId ?? ''}`;
  }

  private persistSearch() {
    this.storage.set(this.searchStorageKey(), {
      searched: this.searchTab.searched,
      request: this.searchTab.request,
      data: this.searchTab.data,
    });
  }

  private restoreSearch() {
    const stored = this.storage.get(this.searchStorageKey());
    if (!stored?.searched) {
      return;
    }
    this.searchTab.searched = true;
    this.searchTab.request = stored.request ?? null;
    this.searchTab.data = stored.data ?? [];
    this.searchTab.count = this.searchTab.data.length;
  }

  onTabChange(tabValue: string) {
    const value = tabValue === 'search' ? 'search' : 'browse';
    if (value === 'browse' && this.browse.weekStartDate) {
      this.weekRestoreDate = this.browse.weekStartDate;
    }
    this.tab.setValue(value, { emitEvent: false });
    this.storage.set(this.tabStorageKey(), value);
  }

  private tabStorageKey(): string {
    return document.location.pathname === '/todo'
      ? 'personal-tasks-tab'
      : 'personal-tasks-tab-claim';
  }

  private viewSettingsKey(): string {
    return this.isPageTodo
      ? 'personal-tasks-my-tasks-view-main'
      : 'personal-tasks-my-tasks-view-claim';
  }

  private defaultView(): MyTasksView {
    return this.isPageTodo ? 'week' : 'all';
  }

  private allowedViews(): MyTasksView[] {
    return this.isPageTodo ? ['week', '3months'] : ['all', 'week', '3months'];
  }

  private async readSavedView(): Promise<MyTasksView> {
    try {
      const value = await this.settings.get(this.viewSettingsKey());
      if (this.allowedViews().includes(value as MyTasksView)) {
        return value as MyTasksView;
      }
    } catch {
      // Missing or unreadable setting uses the level default.
    }
    return this.defaultView();
  }

  private async onViewRangeChange(value: MyTasksView) {
    if (!value || !this.allowedViews().includes(value)) {
      return;
    }
    if (value === 'week' || value === '3months') {
      this.weekRestoreDate = value === 'week' ? null : this.weekRestoreDate;
      this.browse.isClndrClicked = false;
      this.browse.dayData = [];
      this.browse.selectedDayDate = null;
      this.browse.weekData = [];
      this.browse.calendarItems = [];
    }
    this.browse.view = value;
    try {
      await this.settings.set(this.viewSettingsKey(), value);
    } catch {
      // Keep the selection for this session when the setting cannot be saved.
    }
    if (value === 'week' || value === '3months') {
      return;
    }
    this.browse.request = this.buildRangeRequest();
    await this.loadBrowse();
  }

  private buildRangeRequest(): TaskRequest {
    if (this.browse.view === 'all') {
      this.browse.selectedDate = null;
      this.browse.title = 'All';
      return this.prepareRequest();
    }
    if (this.browse.view === '3months') {
      const range = this.threeMonthWindow();
      this.browse.selectedDate = null;
      this.browse.title = this.formatTaskWeek(range.start, range.end);
      return this.prepareRequest(range.start, range.end);
    }
    const start =
      this.browse.weekStartDate ?? DateEx.getStartOfWeek(new Date());
    const end = this.browse.weekEndDate ?? DateEx.getEndOfWeek(start);
    return this.prepareRequest(start, end);
  }

  private threeMonthWindow(): { start: Date; end: Date } {
    if (this.browse.monthStartDate && this.browse.monthEndDate) {
      return {
        start: this.browse.monthStartDate,
        end: this.browse.monthEndDate,
      };
    }
    const today = new Date();
    return {
      start: new Date(today.getFullYear(), today.getMonth() - 1, 1),
      end: new Date(today.getFullYear(), today.getMonth() + 2, 0),
    };
  }

  private displayedBrowseTasks(): TaskResponse[] {
    if (
      (this.browse.view === 'week' || this.browse.view === '3months') &&
      this.browse.isClndrClicked
    ) {
      return this.browse.dayData;
    }
    return this.browse.data;
  }

  protected get weekRecordPeriod(): string | null {
    if (this.browse.view !== 'week' && this.browse.view !== '3months') {
      return null;
    }
    if (this.browse.isClndrClicked && this.browse.selectedDayDate) {
      return this.formatTaskDay(this.browse.selectedDayDate);
    }
    if (this.browse.view === '3months') {
      const range = this.threeMonthWindow();
      if (!range.start || !range.end) {
        return null;
      }
      return this.formatTaskWeek(range.start, range.end);
    }
    if (this.browse.weekStartDate && this.browse.weekEndDate) {
      return this.formatTaskWeek(
        this.browse.weekStartDate,
        this.browse.weekEndDate,
      );
    }
    return null;
  }

  private formatTaskDay(date: Date): string {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  private formatTaskWeek(start: Date, end: Date): string {
    const startOptions: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
    };
    if (start.getFullYear() !== end.getFullYear()) {
      startOptions.year = 'numeric';
    }
    const startText = start.toLocaleDateString('en-US', startOptions);
    const endText = end.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${startText} – ${endText}`;
  }

  private filterTasksByDate(
    tasks: TaskResponse[],
    targetDate: Date,
  ): TaskResponse[] {
    return (tasks ?? []).filter((task) => {
      const due = new Date(task.dateDue);
      return (
        due.getFullYear() === targetDate.getFullYear() &&
        due.getMonth() === targetDate.getMonth() &&
        due.getDate() === targetDate.getDate()
      );
    });
  }

  private taskCountTitle(count: number): string {
    return `${count} Task${count === 1 ? '' : 's'}`;
  }

  private refreshActiveTab(): Promise<void> {
    // 349495 3.8.007 keeps Search tasks columns. A save only removes a row that no longer matches. My tasks still refetches.
    if (this.tab.value === 'search') {
      this.applySearchPatch();
      return Promise.resolve();
    }
    this.applySearchPatch();
    return this.loadBrowse();
  }

  get filteredCalendarItems() {
    if (
      this._lastCalendarItems === this.browse.calendarItems &&
      this._lastIncludeCompletedCal === this.browse.includeCompleted &&
      this._lastSelectedDate === this.browse.selectedDate
    ) {
      return this._lastFilteredCalendarItems;
    }

    const items = this.browse.calendarItems.map((item) => {
      if (!this.browse.includeCompleted) {
        return { ...item, completed: 0 };
      }
      return item;
    });
    if (
      (this.browse.isClndrClicked || this.browse.isMarkCompleteEventFired) &&
      this.browse.selectedDate &&
      Array.isArray(items)
    ) {
      for (const item of items) {
        item.active =
          DateEx.formatShortDate(new Date(item.date)) ===
          this.browse.selectedDate;
      }
    }
    this._lastCalendarItems = this.browse.calendarItems;
    this._lastIncludeCompletedCal = this.browse.includeCompleted;
    this._lastSelectedDate = this.browse.selectedDate;
    this._lastFilteredCalendarItems = items;
    return items;
  }

  get filteredBrowseTasks() {
    const source = this.displayedBrowseTasks();
    if (
      this._lastBrowseTaskData === source &&
      this._lastBrowseIncludeCompleted === this.browse.includeCompleted
    ) {
      return this._lastBrowseRows;
    }
    const tasks = this.browse.includeCompleted
      ? source
      : source.filter(
          (task) => (task.status || '').toLowerCase() !== 'completed',
        );
    this._lastBrowseTaskData = source;
    this._lastBrowseIncludeCompleted = this.browse.includeCompleted;
    this._lastBrowseRows = this.mapTaskRows(tasks, false);
    return this._lastBrowseRows;
  }

  get filteredSearchTasks() {
    if (
      this._lastSearchTaskData === this.searchTab.data &&
      this._lastSearchRequest === this.searchTab.request
    ) {
      return this._lastSearchRows;
    }
    this._lastSearchTaskData = this.searchTab.data;
    this._lastSearchRequest = this.searchTab.request;
    this._lastSearchRows = this.mapTaskRows(this.searchTab.data, true);
    return this._lastSearchRows;
  }

  private mapTaskRows(tasks: TaskResponse[], searchMode: boolean) {
    const request = searchMode ? this.searchTab.request : this.browse.request;
    return tasks
      .map((task) => {
        let recordSource;
        let recordId;
        let fileNumber: any = null;

        switch (true) {
          case !!task.claimUID:
            recordSource = 'J';
            recordId = task.claimUID;
            break;
          case !!task.adaEventNumber:
            recordSource = 'A';
            recordId = task.adaEventNumber;
            break;
          case !!task.leaveNumber:
            recordSource = 'L';
            recordId = task.leaveNumber;
            break;
          case !!task.absenceUID:
            recordSource = 'T';
            recordId = task.absenceUID;
            fileNumber = task.absenceUID;
            break;
          case !!task.adaTamsRequestID:
            recordSource = 'R';
            recordId = task.adaTamsRequestID;
            fileNumber = task.adatamsEventNumber;
            break;
          default:
            recordSource = '';
            recordId = null;
            break;
        }

        return {
          title: task.title || 'Untitled',
          taskUid: task.taskUID,
          fileNumber: task.taskFor || fileNumber || '',
          firstName: task.firstName,
          lastName: task.lastName,
          dueDate: task.dateDue,
          dateCreated: task.dateCreated,
          dateUpdated: task.dateUpdated,
          status: this.getStatusLabel(task.status, task.dateDue),
          sendEmail: task.emailReminder,
          update: 'Update',
          message: task.description,
          navigatingRecID: task.recordID,
          recordSource,
          recordId,
          format: task.format,
        };
      })
      .filter((mappedTask) => {
        if (searchMode && request?.taskStatus === 1) {
          return mappedTask.status !== 'Past due';
        }

        if (searchMode && request?.taskStatus === 3) {
          return mappedTask.status === 'Past due';
        }

        if (
          searchMode &&
          !this.isPageTodo &&
          this.claim &&
          request?.pastDue === true
        ) {
          return this.matchesCurrentClaim(mappedTask);
        }

        return true;
      });
  }

  private matchesCurrentClaim(mappedTask: any): boolean {
    if (!this.claim || !this.claim.recordSource) {
      return false;
    }

    if (leaveLink(this.claim.recordSource) && mappedTask.recordSource === 'L') {
      const claimLeave = numericAdaOrLeaveKeyFromClaim(this.claim);
      const taskLeave = numericAdaOrLeaveKeyFromClaim({
        fileNumber: mappedTask.recordId ?? mappedTask.fileNumber,
      });
      return (
        claimLeave != null && taskLeave != null && claimLeave === taskLeave
      );
    }

    // First, try to match by fileNumber if both task and claim have fileNumber
    const taskFileNumber = mappedTask.fileNumber;
    const claimFileNumber = this.claim.fileNumber;

    if (taskFileNumber && claimFileNumber) {
      return taskFileNumber === claimFileNumber;
    }

    // Fall back to recordSource and recordId comparison
    const taskRecordSource = mappedTask.recordSource;
    const taskRecordId = mappedTask.recordId?.toString();
    const claimRecordId = this.claim.recordId?.toString();

    return (
      taskRecordSource === this.claim.recordSource &&
      taskRecordId === claimRecordId
    );
  }

  private getClaimSpecificFields(): any {
    const claim = this.view.currentClaim();
    const z: TaskRequest = {
      claimUID: null,
      absenceUID: null,
      employeeUID: null,
      adaEventNumber: null,
      leaveNumber: null,
      adaTamsRequestID: null,
    };
    if (!claim) {
      return { ...z };
    }

    switch (claim.recordSource) {
      case 'J':
        return { ...z, claimUID: claim.recordId };
      case 'T':
        return {
          ...z,
          absenceUID: claim.fileNumber,
          employeeUID: claim.hrMasterUid ? Number(claim.hrMasterUid) : null,
        };
      case 'A':
        return {
          ...z,
          adaEventNumber: numericAdaOrLeaveKeyFromClaim(claim),
        };
      case 'L':
        return {
          ...z,
          leaveNumber: numericAdaOrLeaveKeyFromClaim(claim),
        };
      case 'R':
        return {
          ...z,
          adaTamsRequestID: claim.recordId,
        };
      default:
        return { ...z, claimUID: claim.recordId };
    }
  }
}
